import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Search } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/* ── ROLE PICKER MODAL ── */
export const RolePickerModal = ({ available, currentRole, onSelect, onClose }) => {
    const { isDark } = useTheme();
    const [q, setQ] = useState('');
    const filtered = available.filter(r => r.toLowerCase().includes(q.toLowerCase()));

    const accentColor = isDark ? '#F59E0B' : '#2563eb';
    const modalBg = isDark ? '#1A1814' : '#ffffff';
    const borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0';
    const borderBottom = isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9';
    const textColor = isDark ? '#fff' : '#0f172a';
    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    const searchBg = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
    const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)';
    const textSecondary = isDark ? 'rgba(255,255,255,0.75)' : '#475569';
    const itemSelectedBg = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.1)';

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: 'fixed', inset: 0, zIndex: 200, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                padding: 24
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 480, background: modalBg,
                    border: `1px solid ${borderColor}`, borderRadius: 20, overflow: 'hidden'
                }}
            >
                <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${borderBottom}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: textColor }}>Select a role</h3>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                            <X size={18} />
                        </button>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: mutedColor }} />
                        <input
                            autoFocus
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search roles…"
                            style={{
                                width: '100%', height: 40, paddingLeft: 36, paddingRight: 14,
                                background: searchBg, border: `1px solid ${borderColor}`,
                                borderRadius: 10, color: textColor, fontSize: 13, outline: 'none', boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>
                <div style={{ maxHeight: 340, overflowY: 'auto', padding: '8px 0' }}>
                    <button
                        onClick={() => onSelect('')}
                        style={{
                            width: '100%', textAlign: 'left', padding: '10px 24px',
                            background: !currentRole ? itemSelectedBg : 'none',
                            border: 'none', color: !currentRole ? accentColor : mutedColor,
                            fontSize: 13, cursor: 'pointer'
                        }}
                        onMouseEnter={e => { if (currentRole) e.currentTarget.style.background = hoverBg; }}
                        onMouseLeave={e => { if (currentRole) e.currentTarget.style.background = 'none'; }}
                    >
                        All roles
                    </button>
                    {filtered.map(role => (
                        <button
                            key={role}
                            onClick={() => onSelect(role)}
                            style={{
                                width: '100%', textAlign: 'left', padding: '10px 24px',
                                background: role === currentRole ? itemSelectedBg : 'none',
                                border: 'none', color: role === currentRole ? accentColor : textSecondary,
                                fontSize: 13, cursor: 'pointer'
                            }}
                            onMouseEnter={e => { if (role !== currentRole) e.currentTarget.style.background = hoverBg; }}
                            onMouseLeave={e => { if (role !== currentRole) e.currentTarget.style.background = 'none'; }}
                        >
                            {role}
                        </button>
                    ))}
                    {filtered.length === 0 && (
                        <div style={{ padding: '12px 24px', fontSize: 13, color: mutedColor }}>No roles found</div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

/* ── LOCATION PICKER MODAL ── */
export const LocationPickerModal = ({ available, currentLocation, onSelect, onClose }) => {
    const { isDark } = useTheme();
    const [q, setQ] = useState('');
    const filtered = available.filter(r => r.toLowerCase().includes(q.toLowerCase()));

    const accentColor = isDark ? '#10B981' : '#059669';
    const modalBg = isDark ? '#1A1814' : '#ffffff';
    const borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0';
    const borderBottom = isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9';
    const textColor = isDark ? '#fff' : '#0f172a';
    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    const searchBg = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
    const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)';
    const textSecondary = isDark ? 'rgba(255,255,255,0.75)' : '#475569';
    const itemSelectedLocBg = isDark ? 'rgba(16,185,129,0.1)' : 'rgba(5,150,105,0.1)';

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                position: 'fixed', inset: 0, zIndex: 200, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
                padding: 24
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 480, background: modalBg,
                    border: `1px solid ${borderColor}`, borderRadius: 20, overflow: 'hidden'
                }}
            >
                <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${borderBottom}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: textColor }}>Select a location</h3>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                            <X size={18} />
                        </button>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: mutedColor }} />
                        <input
                            autoFocus
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search locations…"
                            style={{
                                width: '100%', height: 40, paddingLeft: 36, paddingRight: 14,
                                background: searchBg, border: `1px solid ${borderColor}`,
                                borderRadius: 10, color: textColor, fontSize: 13, outline: 'none', boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>
                <div style={{ maxHeight: 340, overflowY: 'auto', padding: '8px 0' }}>
                    <button
                        onClick={() => onSelect('')}
                        style={{
                            width: '100%', textAlign: 'left', padding: '10px 24px',
                            background: !currentLocation ? itemSelectedLocBg : 'none',
                            border: 'none', color: !currentLocation ? accentColor : mutedColor,
                            fontSize: 13, cursor: 'pointer'
                        }}
                        onMouseEnter={e => { if (currentLocation) e.currentTarget.style.background = hoverBg; }}
                        onMouseLeave={e => { if (currentLocation) e.currentTarget.style.background = 'none'; }}
                    >
                        Any Location
                    </button>
                    {filtered.map(loc => (
                        <button
                            key={loc}
                            onClick={() => onSelect(loc)}
                            style={{
                                width: '100%', textAlign: 'left', padding: '10px 24px',
                                background: loc === currentLocation ? itemSelectedLocBg : 'none',
                                border: 'none', color: loc === currentLocation ? accentColor : textSecondary,
                                fontSize: 13, cursor: 'pointer'
                            }}
                            onMouseEnter={e => { if (loc !== currentLocation) e.currentTarget.style.background = hoverBg; }}
                            onMouseLeave={e => { if (loc !== currentLocation) e.currentTarget.style.background = 'none'; }}
                        >
                            {loc}
                        </button>
                    ))}
                    {filtered.length === 0 && (
                        <div style={{ padding: '12px 24px', fontSize: 13, color: mutedColor }}>No locations found</div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};
